/**
 * Formatting helpers shared by every Container Management view.
 *
 * Kubernetes reports raw values (kibibytes, RFC3339 timestamps); these render
 * them the way an operator reading `kubectl` output expects.
 */

const KIB_PER_MIB = 1024;
const MIB_PER_GIB = 1024;

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "967420Ki" -> "945 MiB". Values that do not parse are passed through
 * untouched rather than rendered as a wrong number.
 */
export const formatMemory = (raw?: string): string => {
    if (!raw) {
        return "";
    }

    const match = /^(\d+)Ki$/.exec(raw);
    if (!match) {
        return raw;
    }

    const mib = parseInt(match[1], 10) / KIB_PER_MIB;

    return mib >= MIB_PER_GIB ? `${(mib / MIB_PER_GIB).toFixed(1)} GiB` : `${Math.round(mib)} MiB`;
};

/**
 * Coarse age in the style of `kubectl get`: "12d", "5h", "3m", "45s".
 * A single unit is enough - this column exists to spot outliers, not to be precise.
 */
export const formatAge = (timestamp?: string, now: number = Date.now()): string => {
    if (!timestamp) {
        return "";
    }

    const started = Date.parse(timestamp);
    if (isNaN(started)) {
        return "";
    }

    const elapsed = Math.max(0, now - started);

    if (elapsed >= DAY) {
        return `${Math.floor(elapsed / DAY)}d`;
    }
    if (elapsed >= HOUR) {
        return `${Math.floor(elapsed / HOUR)}h`;
    }
    if (elapsed >= MINUTE) {
        return `${Math.floor(elapsed / MINUTE)}m`;
    }

    return `${Math.floor(elapsed / SECOND)}s`;
};
